import * as React from 'react';
import styled, { css } from 'styled-components';
import { generateId } from '@agilepoint/utils-common';
import { NotificationItem } from './notification-item';
import { INotificationListProps, IDefaultNotificationConfigs, INotificationInfo, NotificationPosition } from './notification-types';
import { withHostableSurface } from '../hostable-surface';

function addUniqueIdForNotification(notification: INotificationInfo) {
    if (notification.id === undefined) {
        notification.id = generateId();
    }
    return notification;
}

interface INotificationListState {
    notifications: Map<string, INotificationInfo>;
}

class NotificationListBase extends React.PureComponent<INotificationListProps, INotificationListState> {
    constructor(props: INotificationListProps) {
        super(props);
        const { eventManager } = this.props;
        this.state = {
            notifications: new Map<string, INotificationInfo>()
        };

        eventManager.subscribe('showNotification', this.addNotifications);
        eventManager.subscribe('removeNotification', this.removeNotification);
    }

    render() {
        const {
            onRenderCloseIcon, onRenderErrorIcon, onRenderInformationIcon, onRenderSuccessIcon,
            onRenderWarningIcon, className, position = 'topLeft' } = this.props;
        const { notifications } = this.state;
        return (
            <div className={className}>
                {
                    Array.from(notifications).map(([id, item]) => (
                        <NotificationItem {...item}
                            key={id}
                            position={position}
                            onClose={this.removeNotification}
                            getDefaultConfigs={this.getDefaultConfigs}
                            onRenderCloseIcon={onRenderCloseIcon}
                            onRenderErrorIcon={onRenderErrorIcon}
                            onRenderSuccessIcon={onRenderSuccessIcon}
                            onRenderWarningIcon={onRenderWarningIcon}
                            onRenderInformationIcon={onRenderInformationIcon} />
                    ))
                }
            </div>
        );
    }

    componentWillUnmount() {
        const { eventManager } = this.props;
        eventManager.unsubscribe('showNotification', this.addNotifications);
        eventManager.unsubscribe('removeNotification', this.removeNotification);
    }

    private addNotifications = (...notificationsInfo: INotificationInfo[]) => {
        const { notifications } = this.state;
        const newNotifications = new Map(notifications);
        notificationsInfo.map(addUniqueIdForNotification).forEach(notification => {
            newNotifications.set(notification.id!, notification);
        });
        this.setState({
            notifications: newNotifications
        });
    }

    private removeNotification = (id?: string) => {
        const { notifications } = this.state;
        let newNotifications = new Map<string, INotificationInfo>();
        if (id !== undefined) {
            if (!notifications.has(id)) {
                return;
            }
            newNotifications = new Map(notifications);
            newNotifications.delete(id);
        }
        this.setState({
            notifications: newNotifications
        });
    }

    private getDefaultConfigs = (): IDefaultNotificationConfigs => {
        const { defaultInformationTimeOut = 5, defaultErrorTimeOut = 5, defaultSuccessTimeOut = 5, defaultWarningTimeOut = 5 } = this.props;
        return {
            defaultInformationTimeOut,
            defaultErrorTimeOut,
            defaultSuccessTimeOut,
            defaultWarningTimeOut
        };
    }
}

function getListPosition(position: NotificationPosition = 'topLeft') {
    switch (position) {
        case 'topRight':
            return css`
                top: 32px;
                right: 32px;
            `;

        case 'bottomLeft':
            return css`
                bottom: 32px;
                left: 32px;
            `;

        case 'bottomRight':
            return css`
                bottom: 32px;
                right: 32px;
            `;

        default:
            return css`
                top: 32px;
                left: 32px;
            `;
    }
}

export const NotificationList = styled(withHostableSurface(NotificationListBase)).attrs(() => ({
    className: String('ap-notification-list')
}))`
    display: inline-block;
    position: absolute;
    max-height: 80%;
    max-width: 550px;
    width: 100%;
    z-index: 99999;
    ${({ position }) => getListPosition(position)};
`;
